import {
  isSupportedDeepLinkTool,
  type DeepLink,
} from "@/components/common/deep-link/deep-link-encoding";
import { decodeCameraParameters } from "@/components/r3f/utils/camera-parameters";
import { decodeOrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import {
  toModeInitialState,
  type ValidSavedView,
} from "@/features/saved-views/apply-saved-view";
import { getMode, isModeName } from "@/modes";
import {
  selectActiveElementFromLookAtId,
  selectModeFromElement,
} from "@/store/mode-selectors";
import { selectActiveArea } from "@/store/selections-selectors";
import type { RootState } from "@/store/store";
import { selectCanUseSnapshotCreation } from "@/store/subscription-features/subscription-features-selectors";
import { SceneFilter } from "@/types/scene-filter";
import { includes } from "@faro-lotv/foundation";
import { isIElementModel3dStream } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import type { SavedViewData } from "@faro-lotv/sphere-viewer-contracts";

/**
 * Converts a legacy deep link into the structured saved view shape.
 *
 * A deep link carries every field as a URL string, three of them in a compact
 * custom encoding. Normalizing it here means the resolver, and everything after
 * it, only ever sees a {@link SavedViewData}, whatever kind of link the app was
 * opened with. Fields a deep link cannot express (`intensity`, `filtering`) stay
 * absent, which `applySavedView` reads as "clear this state" — matching how the
 * app opens a legacy link today.
 *
 * This is the exact inverse of `captureModeState` in `capture-saved-view.ts`.
 * The round trip is lossless because `urlEncodeNumber` already quantizes at
 * encode time.
 *
 * @param link The decoded deep link params.
 * @returns the structured view, or undefined if an encoded field is malformed.
 */
export function toSavedViewData(link: DeepLink): SavedViewData | undefined {
  try {
    return {
      id: link.id,
      lookAtId: link.lookAtId,
      mode: link.mode,
      tool: link.tool,
      cadId: link.cadId,
      scene: link.scene,
      rightId: link.rightId,
      rightScene: link.rightScene,
      syncRot: link.syncRot,
      syncWaypoint: link.syncWaypoint,
      camera: decodeCameraParameters(link.camera),
      clipping: decodeOrientedBoundingBox(link.clipping),
      rightCam: decodeCameraParameters(link.rightCam),
    };
  } catch {
    // A malformed geometry encoding makes the whole link unusable. Reporting it
    // as "no view" rather than as an error is deliberate: see the resolver
    // below, where undefined and Error mean different things.
    return undefined;
  }
}

/**
 * Resolves a candidate saved view against the currently loaded project.
 *
 * This is the counterpart the api cannot provide: the contract keeps `mode`,
 * `tool` and `scene` plain strings because only the viewer knows its own enums
 * and its own project. Narrowing them, and checking that the view still refers
 * to something that exists, happens here — which is what turns a
 * {@link SavedViewData} into the {@link ValidSavedView} `applySavedView` takes.
 *
 * Two invariants matter for the caller:
 *
 * - **A candidate wins; the cache answers when there is none.** The provider
 *   caches the resolved view in the store and then strips the link from the
 *   location, so from that point on there is no candidate and the cache is what
 *   keeps the resolution stable. It must not outrank a candidate, though: a
 *   session can open a second view - another share link, or a pick from a list of
 *   saved views - and answering that with the first one would strand the user on
 *   the view they navigated away from.
 * - **Every failure is `undefined`, never an error.** This runs on every store
 *   change while the project loads, so "the element is not in the project" is
 *   indistinguishable from "the project has not finished loading". Reporting a
 *   failure would flash the invalid-link banner during a normal load. A link the
 *   app can decide about synchronously — params that are not a deep link at all —
 *   is the only error case, and `decodeDeepLink` already reports it.
 *
 * @param candidate The view the app was opened with, if any.
 * @returns a resolver for the view to open, or undefined if there is none to open.
 */
export function selectResolvedSavedView(candidate: SavedViewData | undefined) {
  return (state: RootState): ValidSavedView | undefined => {
    if (!candidate) return state.mode.savedView;

    const view: SavedViewData = { ...candidate };

    // Check whether the active element exists and is found in the project.
    // If not, it can be inferred by the lookAtId.
    const activeElement =
      (view.id && selectIElement(view.id)(state)) ??
      selectActiveElementFromLookAtId(view.lookAtId)(state);
    if (!activeElement) return;

    if (isIElementModel3dStream(activeElement)) {
      view.cadId = activeElement.id;
      view.scene ??= SceneFilter.Cad;
      const activeArea = selectActiveArea(state);
      if (!activeArea) return;
      view.id = activeArea.id;
    } else {
      view.id = activeElement.id;
    }

    // If a mode was not specified infer it from the activeElement and the lookAt element
    const mode =
      view.mode ?? selectModeFromElement(activeElement, view.lookAtId)(state);
    if (!isModeName(mode)) return;

    const modeToOpen = getMode(mode);

    // Check if the mode can be opened with the current element
    if (
      modeToOpen.canBeStartedWith &&
      !modeToOpen.canBeStartedWith(activeElement, state)
    ) {
      return;
    }

    // A mode validates its own share of the view, and has two ways of rejecting
    // it: returning undefined, or throwing. Split mode uses both - undefined for
    // a view with no comparison element, a throw for one naming an element the
    // project does not have.
    if (modeToOpen.initialState) {
      try {
        const modeState = modeToOpen.initialState.parse(
          state,
          toModeInitialState(view),
        );
        if (modeState === undefined) return;
      } catch {
        return;
      }
    }

    const tool =
      isSupportedDeepLinkTool(view.tool) &&
      // Snapshot tool requires specific plan to be activated
      selectCanUseSnapshotCreation(state)
        ? view.tool
        : undefined;

    return {
      ...view,
      mode,
      tool,
      scene: toSceneFilter(view.scene),
      rightScene: toSceneFilter(view.rightScene),
    };
  };
}

/**
 * Narrows a stored scene filter to the viewer's own enum.
 *
 * The contract keeps it a plain string, and the modes already fall back to their
 * own default for a value they do not know, so an unrecognized filter is dropped
 * rather than rejecting the whole view: the view still opens, just with the
 * mode's default scene.
 *
 * @param scene The scene filter stored in the view.
 * @returns the scene filter, or undefined if the viewer does not know it.
 */
function toSceneFilter(scene: string | undefined): SceneFilter | undefined {
  return includes(Object.values(SceneFilter), scene) ? scene : undefined;
}
